import { portal } from '@/helpers/Environment'
import {
  createConsoleLogger,
  getClient,
  type IConfigCatClient,
  type IConfigCatLogger,
  LogLevel,
  PollingMode,
  User,
} from 'configcat-js'

export type Flags = Partial<{
  auto_login_external: boolean
  extend_cart_timer: boolean
}>

export const configCatRequest = fetchConfigCatFlags()

/**
 * Asynchronous access to flags outside Vue components. The caller must be sure that flags have been initialized.
 *
 * Prefer `this.flags` inside Vue components, or `configCatRequest` if flags might be uninitialized.
 */
export let flags: Flags | undefined

export async function initializeFlags() {
  flags = await configCatRequest
}

async function fetchConfigCatFlags(): Promise<Flags> {
  const client = initializeClient()
  await client.forceRefreshAsync()
  const response = await client.getAllValuesAsync(getUser())

  const result = {}
  for (const { settingKey, settingValue } of response) {
    result[settingKey] = settingValue
  }
  return result
}

function initializeClient(): IConfigCatClient {
  const keys = {
    development: 'configcat-sdk-1/rjTdCIqCjEaOl3B3HomA_w/Jj42552GM0SRRLzDbtjVtg',
    production: 'configcat-sdk-1/rjTdCIqCjEaOl3B3HomA_w/_u84IgxIwESKSjk_rz9cCA',
    staging: 'configcat-sdk-1/rjTdCIqCjEaOl3B3HomA_w/NMlYhNuBb0uEFurJBG1krw',
  }

  // Use the strictest flag configuration as the fallback.
  const key = keys[window.tixEnv] ?? keys.production

  return getClient(key, PollingMode.ManualPoll, {
    logger: initializeLogger(window.tixEnv),
  })
}

function initializeLogger(env: string): IConfigCatLogger | undefined {
  // Should not expose our flags and targeting rules to users.
  if (env !== 'production') {
    return createConsoleLogger(LogLevel.Warn)
  }
}

function getUser(): User {
  // The 'placeholder' prevents warnings for flags that filter users by email address.
  return new User('', 'placeholder', '', { tenant: portal.slug, tags: [] })
}
